import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import PercentIcon from "@mui/icons-material/Percent";

import { ButtonUI } from "../../../../../../Common";
import ComponentSuspense from "../../../../../../Common/ComponentSuspense";
import S3FileBrowser from "../../../../../Browsers/S3FileBrowser";
import SSHFileBrowser from "../../../../../Browsers/SSHFileBrowser";
import AzureFileBrowser from "../../../../../Browsers/AzureFileBrowser";
import SharePointBrowser from "../../../../../Browsers/SharePointBrowser";

import { setPrevState } from "../../../../../../../util/common";
import { regexDelimiters, regexFilePath, regexName } from "../../../../../../../util/regularExpress";
import { SKIP_FILE_CUSTOM_VALUE_LIMIT } from "../../../../../../../util/Data/DataLoad";
import { CONVERSION_OPTIONS, JOB_BY_TYPE, JOB_PURPOSE_CONVERSION, browserStateVar } from "../../../../utils/JobData";
import KMKGroupSelect from "../../../../../../Common/KMKGroupSelect";

const MyAceEditor = React.lazy(() => import("../../../../../../Common/AceEditor"));

const sqlJobs = ["snowflake", "salesforce", "veevavault", "mysql"];
const mysqlUnsupportedEncodings = ["UTF16BE", "UTF16LE", "UTF32"];

const CustomPaper = React.memo((props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
});

const useStyles = makeStyles()((theme) => ({
  queryArea: {
    width: "100% !important",
    height: "173px !important",
    border: "1px solid",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
  },
}));

const BrowseInput = ({ exchangeJobState, setExchangeJobState, $sourceList }) => {
  const { source, sourcePath, sourcePathError, bucket, locationList, container, docuList } = exchangeJobState;

  const [tmpBucket, setTmpBucket] = useState(bucket);
  const [openBrowser, setOpenBrowser] = useState({
    sftpBrowser: false,
    azureBrowser: false,
    spBrowser: false,
    s3Browser: false,
  });

  const handlePathChange = useCallback(
    (e) => {
      if (regexFilePath(e.target.value, true)) {
        setPrevState(setExchangeJobState, {
          sourcePath: e.target.value,
          sourcePathError: e.target.value[e.target.value.length - 1] === ".",
        });
      }

      if (e.target.value === "") setPrevState(setExchangeJobState, { sourcePath: "" });
    },
    [setExchangeJobState]
  );

  const handleBrowser = useCallback((browser, val) => {
    const prop = browserStateVar[browser];
    setOpenBrowser((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = key === prop ? val : false;
      });

      return newState;
    });
  }, []);

  const handleBrowseClick = useCallback(() => {
    // find selected source and set bucket and open according browser
    const locationDetail = $sourceList.find((locationDetail) => locationDetail.Stage_id === source);
    if (locationDetail) {
      const Detail = locationDetail.Detail;
      setTmpBucket(Detail.url);
      handleBrowser(Detail.type.toLowerCase(), true);
    }
  }, [$sourceList, source, handleBrowser]);

  const handleS3Browser = useCallback((val) => handleBrowser("s3", val), [handleBrowser]);
  const handleAzureBrowser = useCallback((val) => handleBrowser("azure", val), [handleBrowser]);
  const handleSharepointBrowser = useCallback((val) => handleBrowser("sharepoint", val), [handleBrowser]);
  const handleSFTPBrowser = useCallback((val) => handleBrowser("sftp", val), [handleBrowser]);

  const setPath = useCallback(
    (pathValue) => setPrevState(setExchangeJobState, { sourcePath: pathValue, bucket: tmpBucket }),
    [setExchangeJobState, tmpBucket]
  );

  return (
    <>
      <FormControl sx={{ width: { xs: "100%", sm: "300px" } }}>
        <TextField
          value={sourcePath || ""}
          required
          fullWidth
          label="Path"
          onChange={handlePathChange}
          onBlur={() => {
            if (sourcePath && sourcePath[0] !== "/")
              setPrevState(setExchangeJobState, { sourcePath: "/" + sourcePath });
          }}
          error={!!sourcePathError}
          helperText={sourcePathError ? "Invalid Path" : "Allowed characters 'A-Z','a-z','0-9','/:.-_' and wildcard(*)"}
        />
      </FormControl>
      <ButtonUI
        disabled={!source}
        color="secondary"
        component="span"
        variant="contained"
        endIcon={<UploadFileIcon />}
        sx={{
          marginTop: "9px",
          ml: 1,
        }}
        onClick={handleBrowseClick}
      >
        Browse
      </ButtonUI>

      {openBrowser.s3Browser && (
        <S3FileBrowser
          setOpenFileBrowser={handleS3Browser}
          url={tmpBucket}
          path={sourcePath}
          setPath={setPath}
          stage={source}
          locationName={source && locationList?.find((loc) => loc.Stage_id === source).Name}
          multipleFileSelection
          fileSelection
        />
      )}
      {openBrowser.sftpBrowser && (
        <SSHFileBrowser
          setOpenFileBrowser={handleSFTPBrowser}
          stage={source}
          locationName={source && locationList?.find((loc) => loc.Stage_id === source).Name}
          url={tmpBucket || "/"}
          pathStr={sourcePath}
          setPath={setPath}
          multipleFileSelection
          fileSelection
        />
      )}
      {openBrowser.azureBrowser && (
        <AzureFileBrowser
          container={container}
          setOpenFileBrowser={handleAzureBrowser}
          stage={source}
          locationName={source && locationList?.find((loc) => loc.Stage_id === source).Name}
          path={`${
            sourcePath
              ? "/" +
                sourcePath
                  .split(",/")
                  .map((p, i) => `${container}${i === 0 ? "" : "/"}${p}`)
                  .join(",/")
              : `/${container}/`
          }`}
          setPath={(pathValue) => {
            setPrevState(setExchangeJobState, {
              sourcePath: pathValue
                .replace(new RegExp(`^/${container}`), "")
                .replace(new RegExp(`,/${container}`, "g"), ","),
            });
          }}
          multipleFileSelection
          fileSelection
        />
      )}
      {openBrowser.spBrowser && (
        <SharePointBrowser
          setOpenFileBrowser={handleSharepointBrowser}
          url={`${
            tmpBucket[tmpBucket.length - 1] === "/" ? tmpBucket.substring(0, tmpBucket.length - 1) : tmpBucket
          }/${docuList}`}
          path={sourcePath}
          setPath={setPath}
          stage={source}
          locationName={source && locationList?.find((loc) => loc.Stage_id === source).Name}
          multipleFileSelection
          fileSelection
        />
      )}
    </>
  );
};

const SourcePropertiesForm = ({
  exchangeJobState,
  setExchangeJobState,
  previousState,
  sourceType,
  fileType,
  delimiter,
  fieldEnclosedBy,
  encoding,
  hasHeaderRow,
}) => {
  const { delimiters, salesforceDelimiters, enclosedByOptions, encodingTypes, fileTypes, errorActions, nullValues } =
    useSelector((state) => state.config);
  const { nullValue, customNullValue, jobPurpose, conversionFrom } = exchangeJobState;

  const destinationType = useMemo(
    () =>
      exchangeJobState.locationList
        .find(({ Stage_id }) => Stage_id === exchangeJobState.destination)
        ?.Detail?.type?.toLowerCase() || "",
    [exchangeJobState.destination, exchangeJobState.locationList]
  );

  const $delimiters = useMemo(
    () => (["salesforce", "veevavault"].includes(destinationType) ? salesforceDelimiters : delimiters),
    [delimiters, destinationType, salesforceDelimiters]
  );

  const handleOnError = useCallback(
    (e) => {
      setPrevState(setExchangeJobState, { onError: e.target.value, onErrorNumber: 0 });
    },
    [setExchangeJobState]
  );

  const handleNullValues = useCallback(
    (e) => {
      setPrevState(setExchangeJobState, {
        nullValue: e.target.value,
        ...(e.target.value !== "custom" ? { customNullValue: "" } : {}),
      });
    },
    [setExchangeJobState]
  );

  const handleCustomNullValue = useCallback(
    (customNullValue) => setPrevState(setExchangeJobState, { customNullValue }),
    [setExchangeJobState]
  );

  const handleSkipFileValueChange = useCallback(
    (e) => {
      if (regexName(e.target.value, "numeric")) {
        const newVal = Number(e.target.value);
        let skilFileErrorMessage = "";
        if (
          newVal < SKIP_FILE_CUSTOM_VALUE_LIMIT[exchangeJobState.onError].min ||
          newVal > SKIP_FILE_CUSTOM_VALUE_LIMIT[exchangeJobState.onError].max
        )
          skilFileErrorMessage = `Threshold value should be between ${
            SKIP_FILE_CUSTOM_VALUE_LIMIT[exchangeJobState.onError].min
          } to ${SKIP_FILE_CUSTOM_VALUE_LIMIT[exchangeJobState.onError].max}.`;
        setPrevState(setExchangeJobState, { onErrorNumber: newVal + "", skipFileError: skilFileErrorMessage });
      }
    },
    [exchangeJobState.onError, setExchangeJobState]
  );

  const OtherOptions = useCallback(
    () => (
      <>
        <FormControl variant="outlined" sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
          <InputLabel>Null Values</InputLabel>
          <Select value={nullValue} label="Null Values" onChange={handleNullValues}>
            {nullValues?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {nullValue === "custom" && (
          <TextField
            required
            label="Custom Null Values"
            value={customNullValue}
            onChange={(e) => handleCustomNullValue(e.target.value)}
            helperText="Please enter the exact comma-separated value"
            sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}
          />
        )}
        <FormControl
          variant="outlined"
          sx={{
            width: { xs: "100%", sm: "300px" },
            m: 1,
            ml: 0,
          }}
        >
          <InputLabel>On Error</InputLabel>
          <Select value={exchangeJobState.onError} label="On Error" onChange={handleOnError}>
            {errorActions.map(({ value, text }) => (
              <MenuItem key={value} value={value}>
                {text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {["SKIP_FILE_NUMBER", "SKIP_FILE_PERCENTAGE"].includes(exchangeJobState.onError) ? (
          <FormControl
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: "200px" },
              m: 1,
              ml: 0,
            }}
          >
            <TextField
              InputProps={{
                inputProps: SKIP_FILE_CUSTOM_VALUE_LIMIT[exchangeJobState.onError],
                endAdornment:
                  exchangeJobState.onError === "SKIP_FILE_PERCENTAGE" ? (
                    <InputAdornment position="end">
                      <PercentIcon />
                    </InputAdornment>
                  ) : (
                    <></>
                  ),
              }}
              type="number"
              value={exchangeJobState.onErrorNumber.toString()}
              required
              fullWidth
              label="Threshold"
              onChange={handleSkipFileValueChange}
              helperText={exchangeJobState.skipFileError}
              error={exchangeJobState.skipFileError !== ""}
            />
          </FormControl>
        ) : (
          <></>
        )}
      </>
    ),
    [
      customNullValue,
      errorActions,
      exchangeJobState.onError,
      exchangeJobState.onErrorNumber,
      exchangeJobState.skipFileError,
      handleCustomNullValue,
      handleNullValues,
      handleOnError,
      handleSkipFileValueChange,
      nullValue,
      nullValues,
    ]
  );

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography
          sx={{
            fontWeight: "bold",
          }}
        >
          Source File Properties -
        </Typography>
      </Grid>
      {jobPurpose === JOB_PURPOSE_CONVERSION ? (
        <Grid size={12}>
          <FormControl
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: "300px" },
              m: 1,
              ml: 0,
            }}
          >
            <InputLabel>Convert From</InputLabel>
            <Select
              label="Convert From"
              onChange={(e) =>
                setPrevState(setExchangeJobState, {
                  conversionFrom: e.target.value,
                })
              }
              value={conversionFrom}
            >
              {CONVERSION_OPTIONS.map(({ Id, Label }) => (
                <MenuItem key={Id} value={Id}>
                  {Label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      ) : (
        <Grid size={12}>
          <FormControl
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: "300px" },
              m: 1,
              ml: 0,
            }}
          >
            <InputLabel>File Type</InputLabel>
            <Select
              label="File Type"
              onChange={(e) =>
                setPrevState(setExchangeJobState, {
                  fileType: e.target.value,
                  isFieldsConfigured:
                    e.target.value !== previousState.fileType ? false : exchangeJobState.isFieldsConfigured,
                })
              }
              value={sourceType.toLowerCase() === "sharepoint" && fileType === "parquet" ? "csv" : fileType}
            >
              {fileTypes
                ?.filter((option) =>
                  ["mysql", "salesforce", "veevavault"].includes(destinationType) ? option.value !== "parquet" : true
                )
                ?.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={sourceType.toLowerCase() === "sharepoint" && option.value === "parquet"}
                  >
                    {option.text}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {fileType === "csv" ? (
            <>
              <FormControl
                variant="outlined"
                sx={{
                  width: { xs: "100%", sm: "300px" },
                  m: 1,
                  ml: 0,
                }}
              >
                <InputLabel>Format delimiter</InputLabel>
                <Select
                  value={delimiter}
                  label="Format delimiter"
                  onChange={(e) => {
                    let flag = e.target.value !== previousState.delimeter ? false : exchangeJobState.isFieldsConfigured;
                    if (e.target.value !== "custom")
                      setPrevState(setExchangeJobState, { customDelimeter: "", isFieldsConfigured: flag });
                    setPrevState(setExchangeJobState, { delimiter: e.target.value, isFieldsConfigured: flag });
                  }}
                >
                  {$delimiters.map((delimeter) => (
                    <MenuItem key={delimeter.value} value={delimeter.value}>
                      {delimeter.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {delimiter === "custom" && (
                <TextField
                  required
                  label="Custom"
                  inputProps={{ maxLength: 2 }}
                  value={exchangeJobState.customDelimeter}
                  onChange={(e) => {
                    let isvalidDelimiter = regexDelimiters(e.target.value);
                    setPrevState(setExchangeJobState, {
                      customDelimeter: isvalidDelimiter ? e.target.value : exchangeJobState.customDelimeter,
                      isFieldsConfigured:
                        isvalidDelimiter && e.target.value !== previousState.delimeter
                          ? false
                          : exchangeJobState.isFieldsConfigured,
                    });
                  }}
                  sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}
                />
              )}
              {!["salesforce", "veevavault"].includes(destinationType) && (
                <>
                  <FormControl
                    variant="outlined"
                    sx={{
                      minWidth: { xs: "100%", sm: "300px" },
                      width: { xs: "100%", sm: "300px" },
                      m: 1,
                      ml: 0,
                    }}
                  >
                    <InputLabel>Text Qualifier</InputLabel>
                    <Select
                      label="Text Qualifier"
                      value={fieldEnclosedBy}
                      onChange={(e) =>
                        setPrevState(setExchangeJobState, {
                          fieldEnclosedBy: e.target.value,
                          isFieldsConfigured:
                            e.target.value !== previousState.fieldEnclosedBy
                              ? false
                              : exchangeJobState.isFieldsConfigured,
                        })
                      }
                    >
                      {enclosedByOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box display="flex" flexWrap="wrap">
                    <FormControl
                      variant="outlined"
                      sx={{
                        width: { xs: "100%", sm: "300px" },
                        m: 1,
                        ml: 0,
                      }}
                    >
                      <InputLabel>Encoding</InputLabel>
                      <Select
                        label="Encoding"
                        value={encoding}
                        onChange={(e) =>
                          setPrevState(setExchangeJobState, {
                            encoding: e.target.value,
                            isFieldsConfigured:
                              e.target.value !== previousState.encoding ? false : exchangeJobState.isFieldsConfigured,
                          })
                        }
                      >
                        {encodingTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormGroup
                      sx={{
                        paddingTop: "10px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hasHeaderRow}
                            onChange={(e) =>
                              setPrevState(setExchangeJobState, {
                                hasHeaderRow: !hasHeaderRow,
                                isFieldsConfigured:
                                  e.target.value !== previousState.hasHeaderRow
                                    ? false
                                    : exchangeJobState.isFieldsConfigured,
                              })
                            }
                          />
                        }
                        label="Has a header row"
                      />
                    </FormGroup>
                  </Box>
                </>
              )}
              {!["mysql", "salesforce", "veevavault"].includes(destinationType) && (
                <Box display="flex" flexWrap="wrap">
                  {OtherOptions()}
                </Box>
              )}
              {destinationType === "mysql" && mysqlUnsupportedEncodings.includes(encoding) && (
                <Stack sx={{ width: "100%", p: "15px 5px" }} spacing={2}>
                  <Alert severity="warning">
                    MySQL does not support <b>UTF-16LE</b> or <b>UTF-32</b> character set.
                  </Alert>
                </Stack>
              )}
            </>
          ) : (
            OtherOptions()
          )}
        </Grid>
      )}
    </Grid>
  );
};

const SourceForm = ({
  source,
  sourceType,
  queryError,
  query,
  delimiter,
  fileType,
  previousState,
  fieldEnclosedBy,
  encoding,
  hasHeaderRow,
  destination,
  resetFiles,
  targetPath,
  locationList,
  setExchangeJobState,
  exchangeJobState,
  loadSchemasClusters,
  runTimeState,
}) => {
  const { classes } = useStyles();
  const queryRef = useRef(null);

  const destinationType = useMemo(
    () =>
      exchangeJobState.locationList
        .find(({ Stage_id }) => Stage_id === exchangeJobState.destination)
        ?.Detail?.type?.toLowerCase() || "",
    [exchangeJobState.destination, exchangeJobState.locationList]
  );

  const $sourceList = useMemo(
    () =>
      exchangeJobState.jobPurpose === JOB_PURPOSE_CONVERSION
        ? [...locationList.filter(({ Detail }) => !sqlJobs.includes(Detail?.type?.toLowerCase()))]
        : [...locationList],
    [exchangeJobState.jobPurpose, locationList]
  );

  const handleConnectionChange = useCallback(
    (e) => {
      const selectedLocation = $sourceList.find((location) => location.Stage_id === e.target.value).Detail;

      if (selectedLocation.type?.toLowerCase() === "snowflake" && e.target.value !== "SNOWFLAKE")
        loadSchemasClusters(selectedLocation.type?.toLowerCase(), e.target.value, true);

      setExchangeJobState((prev) => {
        return {
          ...prev,
          source: e.target.value,
          sourceType: selectedLocation.type.toLowerCase(),
          ...(e.target.value !== prev.source
            ? {
                query: "",
                filename: "",
                sourcePath: "",
                container: "",
                sourcePathError: false,
                queryError: false,
              }
            : {}),
          container: selectedLocation.container,
          docuList: selectedLocation.docuList,
          destination:
            selectedLocation.type.toLowerCase() === "snowflake" &&
            destination?.toString()?.toLowerCase() === "snowflake"
              ? ""
              : destination,
          targetPath:
            selectedLocation.type.toLowerCase() === "snowflake" &&
            destination?.toString()?.toLowerCase() === "snowflake"
              ? ""
              : targetPath,
        };
      });
    },
    [$sourceList, destination, loadSchemasClusters, setExchangeJobState, targetPath]
  );

  const handleOnChangeExternal = useCallback(
    async (e, value) => {
      const database = value?.groupBy?.toUpperCase() || "";
      const schema = value?.title?.toUpperCase() || "";

      setPrevState(setExchangeJobState, {
        schemaName: schema,
        groupBy: database,
        showSchemaError: false,
        isFieldsConfigured: value?.title !== previousState.schemaName ? false : exchangeJobState.isFieldsConfigured,
      });
    },
    [exchangeJobState.isFieldsConfigured, previousState.schemaName, setExchangeJobState]
  );

  const showSourceProperties = useMemo(
    () =>
      source &&
      (exchangeJobState?.jobPurpose === JOB_PURPOSE_CONVERSION ||
        ["mysql", "snowflake"].includes(destinationType) ||
        (["salesforce", "veevavault"].includes(destinationType) && sourceType !== "snowflake")),
    [destinationType, exchangeJobState?.jobPurpose, source, sourceType]
  );

  return (
    <Grid container direction={"row"} spacing={0}>
      <Grid size={12}>
        <Grid container spacing={2}>
          <Grid>
            <FormControl sx={{ width: { xs: "100%", sm: "300px" } }}>
              <InputLabel required>Connection</InputLabel>
              <Select
                value={source}
                label="Connection"
                onChange={handleConnectionChange}
                required
                MenuProps={{ sx: { height: "220px" } }}
              >
                {$sourceList.map((option) => (
                  <MenuItem key={option.Stage_id} value={option.Stage_id}>
                    {option.Name}
                    {option?.IsDataLake === 1 &&
                      (option?.Detail.type.toLowerCase() === "s3" ? (
                        <img src="/images/aws-s3-logo.png" alt="S3" style={{ width: "20px", marginLeft: "10px" }} />
                      ) : (
                        <img src="/images/azure-logo.png" alt="Azure" style={{ width: "20px", marginLeft: "10px" }} />
                      ))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {sourceType.toLowerCase() === "snowflake" && source !== "SNOWFLAKE" && (
            <Grid>
              <FormControl
                variant="outlined"
                error={exchangeJobState?.showSchemaError}
                sx={{ width: { xs: "100%", sm: "300px" } }}
              >
                <KMKGroupSelect
                  options={exchangeJobState?.externalSnowflakeSchemas}
                  label="Schema"
                  value={{ title: exchangeJobState?.schemaName, groupBy: exchangeJobState?.groupBy }}
                  handleChange={handleOnChangeExternal}
                  error={exchangeJobState?.showSchemaError}
                  errorText={"Previously selected schema no longer exists"}
                  noOptionsText={"No schema found"}
                  PaperComponent={CustomPaper}
                  fullWidth
                  required
                />
              </FormControl>
            </Grid>
          )}
          <Grid>
            {sourceType && !sqlJobs.includes(sourceType.toLowerCase()) && (
              <BrowseInput
                exchangeJobState={exchangeJobState}
                setExchangeJobState={setExchangeJobState}
                $sourceList={$sourceList}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      {sourceType && !sqlJobs.includes(sourceType.toLowerCase()) ? (
        <Grid size={12}>
          <Grid container spacing={2}>
            <Grid>
              <Box sx={{ width: { xs: "100%", sm: "300px" } }}></Box>
            </Grid>
            <Grid>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!resetFiles}
                      onChange={(e) => setExchangeJobState((prev) => ({ ...prev, resetFiles: !prev.resetFiles }))}
                    />
                  }
                  label="Skip previously processed files"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
      {sourceType && sqlJobs.includes(sourceType.toLowerCase()) && (
        <>
          <Grid size={12}>
            <Typography color={"#00000099"} padding={0} variant="subtitle2" component="div">
              Query *
            </Typography>
          </Grid>
          <Grid
            sx={{ paddingTop: "2px !important", maxWidth: { xs: "100%", sm: "1200px" } }}
            size={12}>
            <ComponentSuspense>
              <MyAceEditor
                fontSize={14}
                className={classes.queryArea}
                label="Query"
                value={query}
                ref={queryRef}
                setQuery={(value) => setPrevState(setExchangeJobState, { query: value })}
                location={exchangeJobState?.source}
                type={sourceType?.toLowerCase() || ""}
                runParams={{
                  jobType: JOB_BY_TYPE["dataExchange"],
                  ...(source !== "SNOWFLAKE"
                    ? {
                        database: exchangeJobState.groupBy,
                        schema: exchangeJobState.schemaName,
                        cluster: runTimeState?.cluster,
                      }
                    : { runJobAsUserId: runTimeState?.runJobAsUserId || null }),
                }}
                onError={(error) => setPrevState(setExchangeJobState, { queryError: error })}
              />
            </ComponentSuspense>
          </Grid>
        </>
      )}
      {showSourceProperties && (
        <Grid size={12}>
          <SourcePropertiesForm
            exchangeJobState={exchangeJobState}
            setExchangeJobState={setExchangeJobState}
            previousState={previousState}
            sourceType={sourceType}
            fileType={fileType}
            delimiter={delimiter}
            fieldEnclosedBy={fieldEnclosedBy}
            encoding={encoding}
            hasHeaderRow={hasHeaderRow}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default React.memo(SourceForm);
